// app/javascript/controllers/public_discounts_controller.js
import {Controller} from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"

export default class extends Controller {
    static targets = [
        "applyPromo", "promoRow", "promoInput",
        "attachmentsBlock", "discountId", "noteInput", "fileInput",
        "applyDiscount"
    ]
    static values = {
        applyPromocodeUrl: String,     // e.g. "/subscription/payment/apply_promocode"
        subscriptionId: Number         // pass once from the page
    }

    connect() {
        // Initial UI state
        this.togglePromoRow()
        this.updateAttachmentVisibility({clear: false})
        if (this.hasAttachmentsBlockTarget) this.preferOfflinePaymentWhenDiscount()
    }

    // --- Promo code ---
    onApplyPromoChange() {
        this.togglePromoRow()
        if (this.applyPromoTarget.checked) this.submitPromoTurboStream()
        else {
            // Clear when turning off, like legacy code, then refresh
            if (this.hasPromoInputTarget) this.promoInputTarget.value = ""
            this.submitPromoTurboStream()
        }
    }

    onPromoInput() {
        // Normalize like before (UPPERCASE + trim) and debounce submit
        if (this.hasPromoInputTarget && this.promoInputTarget.value) {
            this.promoInputTarget.value = this.promoInputTarget.value.trim().toUpperCase()
        }
        this.debounce(() => this.submitPromoTurboStream(), 250)
    }

    togglePromoRow() {
        if (!this.hasPromoRowTarget || !this.hasApplyPromoTarget) return
        const show = this.applyPromoTarget.checked
        this.promoRowTarget.classList.toggle("d-none", !show)
    }

    async submitPromoTurboStream() {
        const url = this.applyPromocodeUrlValue
        if (!url) return

        const promocode = this.hasPromoInputTarget ? this.promoInputTarget.value : ""
        const subscriptionId = this.subscriptionIdValue || this.readSubscriptionIdFromDOM()

        const formData = new FormData()
        formData.append("promocode", promocode)
        formData.append("subscription_id", subscriptionId)

        const res = await fetch(url, {
            method: "POST",
            headers: {
                "Accept": "text/vnd.turbo-stream.html",
                "X-CSRF-Token": this.csrfToken()
            },
            body: formData,
            credentials: "same-origin"
        })

        if (!res.ok) return
        const html = await res.text()
        // Let Turbo process the <turbo-stream> updates from the response
        Turbo.renderStreamMessage(html)
    }

    // --- Backend-only discounts block ---
    onDiscountChange() {
        this.updateAttachmentVisibility()
        this.preferOfflinePaymentWhenDiscount()
        this.refreshPriceFrameIfPresent()
    }

    updateAttachmentVisibility({clear = true} = {}) {
        if (!this.hasAttachmentsBlockTarget) return
        const show = this.hasApplyDiscountTarget ? this.applyDiscountTarget.checked : false

        this.attachmentsBlockTarget.classList.toggle("d-none", !show)
        if (this.hasFileInputTarget) this.fileInputTarget.disabled = !show
        if (show) this.preselectSoleDiscount()
        if (!show && clear) {
            this.resetFileInputs()
            this.clearDiscountFields()
        }
    }

    preselectSoleDiscount() {
        if (!this.hasDiscountIdTarget) return
        if (this.discountIdTargets.length !== 1) return
        const radio = this.discountIdTargets[0]
        if (!radio.checked && !radio.disabled) radio.checked = true
    }

    clearDiscountFields() {
        if (this.hasDiscountIdTarget) this.discountIdTargets.forEach(rb => { rb.checked = false })
        if (this.hasNoteInputTarget) this.noteInputTarget.value = ""
    }

    preferOfflinePaymentWhenDiscount() {
        // Mirror legacy behavior:
        // - Check "offline/bank" method
        // - Disable other methods while discount attachments are shown
        const attachmentsShown = !this.attachmentsBlockTarget?.classList?.contains("d-none")
        const inputs = document.querySelectorAll(".choose-payment-method")
        if (inputs.length === 0) return

        if (attachmentsShown) {
            // Try by value first; fall back to legacy id if needed
            let picked = Array.from(inputs).find(i => ["offline", "bank"].includes(i.value)) ||
                document.getElementById("choose_payment_form_payment_method_offline")
            if (picked && !picked.checked) picked.checked = true
            inputs.forEach(i => {
                if (i !== picked) i.disabled = true
            })
            // Notify payment controller about change (so explainers/highlight update)
            inputs[0]?.dispatchEvent(new Event("change", {bubbles: true}))
        } else {
            inputs.forEach(i => {
                i.disabled = false
            })
        }
    }

    resetFileInputs() {
        if (!this.hasAttachmentsBlockTarget) return
        // Same trick as legacy CoffeeScript: fully reset file inputs
        this.attachmentsBlockTarget.querySelectorAll('input[type="file"]').forEach((input) => {
            const form = document.createElement("form")
            const parent = input.parentNode
            parent.insertBefore(form, input)
            form.appendChild(input)
            form.reset()
            parent.insertBefore(input, form)
            parent.removeChild(form)
        })
    }

    // --- Helpers ---
    refreshPriceFrameIfPresent() {
        const frame = document.getElementById("price_calc")
        if (frame && typeof frame.reload === "function") frame.reload()
    }

    csrfToken() {
        const el = document.querySelector("meta[name='csrf-token']")
        return el && el.content
    }

    debounce(fn, wait) {
        clearTimeout(this._debounceTimer)
        this._debounceTimer = setTimeout(fn, wait)
    }

    readSubscriptionIdFromDOM() {
        // Back-compat with legacy selector
        const el = document.getElementById("choose_payment_form_subscription_id")
        return el ? el.value : ""
    }
}
